/**
 * অবস্থা ২ · লোডিং — `P0 States § ৩ · লোডিং — স্কেলিটন` (Loading · Results).
 *
 * Frame note: "Skeletons shaped like the real cards, not grey rectangles — the layout
 * is already learned by the time content lands. The query stays in the field, so
 * nothing is lost if it fails."
 *
 * Loading is one of the two universal rows: every one of the seven surfaces is `হ্যাঁ`.
 * That is why the count is 45 and not 11 — see matrix.ts.
 *
 * DEVIATION, reported: `@oprappo/ui`'s `Skeleton` draws § 07's card — a 15px title bar,
 * 12px bars below, a hairline footer group, and a 1.6s shimmer. § ৩ draws a
 * DIFFERENT card: a 44px thumbnail beside a 14px/11px stack with a 52px trailing bar,
 * a hairline footer of three short bars, and no shimmer at all — the third card is
 * dimmed with `opacity:.6` instead. The two are different drawings of the same idea,
 * so the frame is transcribed here and `Skeleton` is left for the surfaces § 07 drew
 * it on. The component is not forked and not modified.
 *
 * `prefers-reduced-motion` needs no guard here because this frame animates nothing.
 *
 * W1-04: the field carries the person's OWN query or nothing — the drawn `এসি মেরামত` was
 * shown to everybody whose query was not passed in — and the progress line is no longer
 * the drawn "finding nearby technicians", which `routes/search` put under every query.
 * The back chevron is a button. See `data.ts § ৩` and `docs/deviations/W1-04.md` (S3).
 */
import { ariaLabel } from '@oprappo/tokens';
import { StateFrame, FooterNote, BackChevron } from './chrome.js';
import { LOADING } from './data.js';

export interface LoadingStateProps {
  /**
   * The query that is being run, kept visible in the field. Localised upstream — this
   * is the user's own words echoed back, never composed.
   */
  queryBn?: string | undefined;
  onBack?: (() => void) | undefined;
}

/**
 * One skeleton card: 44px thumb, two bars, an optional 52px trailing bar.
 *
 * The bar widths arrive as Tailwind classes (`w-[64%]`, `w-[38px]`) rather than as CSS
 * width strings. They are drawn values, not data — § ৩ gives each bar a width — and a
 * class keeps them out of a `style` attribute, which the CSP governs under `style-src`
 * (`apps/web/public/_headers`). The percentages and pixel counts are unchanged.
 */
function SkeletonCard({
  titleWidthClass,
  metaWidthClass,
  trailing,
  footer,
  dim,
}: {
  titleWidthClass: string;
  metaWidthClass: string;
  trailing: boolean;
  footer: readonly string[];
  dim?: boolean;
}): JSX.Element {
  return (
    <div
      className={'bg-white border border-border p-[13px]' + (dim === true ? ' opacity-60' : '')}
      aria-hidden
    >
      <div className="flex gap-[12px] items-start">
        <div className="w-[44px] h-[44px] bg-sand-deep flex-none" />
        <div className="flex-1">
          <div className={`h-[14px] bg-border ${titleWidthClass}`} />
          <div className={`h-[11px] bg-sand-deep mt-[9px] ${metaWidthClass}`} />
        </div>
        {trailing ? (
          <div className="w-[52px] flex-none">
            <div className="h-[14px] bg-border" />
          </div>
        ) : null}
      </div>
      {footer.length > 0 ? (
        <div className="border-t border-border mt-[12px] pt-[11px] flex gap-[12px]">
          {footer.map((w) => (
            <div key={w} className={`h-[11px] bg-sand-deep ${w}`} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function LoadingState({ queryBn, onBack }: LoadingStateProps = {}): JSX.Element {
  return (
    <StateFrame stateKey="loading">
      <div className="px-[16px] pt-[16px]">
        <div className="flex gap-[12px] items-center mb-[14px]">
          <button
            type="button"
            aria-label={ariaLabel.back}
            onClick={onBack}
            className="bg-transparent border-0 p-0 rounded-none flex-none"
          >
            <BackChevron />
          </button>
          <div className="flex-1 box-content border border-border bg-white h-[46px] flex items-center px-[13px] font-anek font-medium text-[16px] leading-[1.75] text-ink">
            {queryBn ?? ''}
          </div>
        </div>
        <div
          role="status"
          aria-busy="true"
          aria-live="polite"
          className="font-anek font-normal text-[14px] leading-[1.75] text-muted pb-[14px]"
        >
          {LOADING.progress_bn}
        </div>
      </div>

      <div className="flex-1 px-[16px] oprappo-scroll">
        <div className="h-[16px] w-[132px] bg-border mb-[14px]" aria-hidden />
        <div className="flex flex-col gap-[11px]">
          <SkeletonCard
            titleWidthClass="w-[64%]"
            metaWidthClass="w-[44%]"
            trailing
            footer={['w-[38px]', 'w-[56px]', 'w-[48px]']}
          />
          <SkeletonCard
            titleWidthClass="w-[52%]"
            metaWidthClass="w-[56%]"
            trailing
            footer={['w-[38px]', 'w-[48px]']}
          />
          <SkeletonCard titleWidthClass="w-[58%]" metaWidthClass="w-[40%]" trailing={false} footer={[]} dim />
        </div>
      </div>

      {/* Naming the worst case is the whole point: a slow connection is the norm. */}
      <FooterNote>{LOADING.footer_bn}</FooterNote>
    </StateFrame>
  );
}
